import CONSTANTS from '../constants/constants';

import { DeploymentUsers } from 'types';

import * as api from 'api/deployments';
import { fetchGoalActions } from 'api/goal_actions';

import { addNewGoalAction } from './goal_actions';
import { updateMemberReferrals } from './referrals';

import { handleSamaritanApiError } from './error';
import { handleSamaritanApiResponse } from './index';

function fetchDeploymentOrgsAction() {
    return {
        type: CONSTANTS.GET_DEPLOYMENT_ORGS
    };
}

function fetchDeploymentUsersAction() {
    return {
        type: CONSTANTS.GET_DEPLOYMENT_USERS
    };
}

function referMemberAction() {
    return {
        type: CONSTANTS.REFER_MEMBER
    };
}

function referMemberFailAction() {
    return {
        type: CONSTANTS.REFER_MEMBER_FAIL
    };
}

function referMemberSuccessAction() {
    return {
        type: CONSTANTS.REFER_MEMBER_SUCCESS
    };
}

function referCareCoordinatorAction() {
    return {
        type: CONSTANTS.REFER_CARE_COORDINATOR
    };
}

function referCareCoordinatorFailAction() {
    return {
        type: CONSTANTS.REFER_CARE_COORDINATOR_FAIL
    };
}

function referCareCoordinatorSuccessAction() {
    return {
        type: CONSTANTS.REFER_CARE_COORDINATOR_SUCCESS
    };
}

export function setDeploymentOrgs(response: object) {
    return {
        type: CONSTANTS.SET_DEPLOYMENT_ORGS,
        payload: response
    };
}

function setDeploymentUsers(response: object) {
    return {
        type: CONSTANTS.SET_DEPLOYMENT_USERS,
        payload: response
    };
}

export function fetchDeploymentOrgsRequest(id: number) {
    return (dispatch: Function) => {
        dispatch(fetchDeploymentOrgsAction());
        return api
            .fetchDeployment(id)
            .then((response: object) => {
                return handleSamaritanApiResponse(response, dispatch);
            })
            .then(
                (responseJson: {
                    deployment: { relations: { organizations: object[] } };
                }) => {
                    if (
                        responseJson.deployment &&
                        responseJson.deployment.relations
                    ) {
                        return dispatch(
                            setDeploymentOrgs(
                                responseJson.deployment.relations.organizations
                            )
                        );
                    } else {
                        return dispatch(setDeploymentOrgs([]));
                    }
                }
            )
            .catch((error: any) => {
                handleSamaritanApiError(error, dispatch);
            });
    };
}

export function fetchDeploymentUsersRequest() {
    return (dispatch: Function) => {
        dispatch(fetchDeploymentUsersAction());
        return api
            .fetchDeploymentUsers()
            .then((response: object) => {
                return handleSamaritanApiResponse(response, dispatch);
            })
            .then((responseJson: { users: DeploymentUsers[] }) => {
                if (responseJson.users) {
                    return dispatch(setDeploymentUsers(responseJson.users));
                } else {
                    return dispatch(setDeploymentUsers([]));
                }
            })
            .catch((error: any) => {
                handleSamaritanApiError(error, dispatch);
            });
    };
}

export function referMemberRequest({
    homeless_id,
    note,
    organization_id,
    referee_id
}: {
    homeless_id: number;
    note: null | string;
    organization_id: number;
    referee_id: number;
}) {
    return async (dispatch: Function) => {
        dispatch(referMemberAction());

        try {
            const response = await api.referMember({
                homeless_id,
                note,
                organization_id,
                referee_id
            });

            if (response && response.ok) {
                const responseJson = await response.json();
                const {
                    ctp_caseload_referral: { id: caseload_referral_id } = {
                        id: null
                    }
                } = responseJson;

                if (caseload_referral_id) {
                    dispatch(referMemberSuccessAction());
                    dispatch(
                        updateMemberReferrals(
                            responseJson.ctp_caseload_referral
                        )
                    );

                    try {
                        const goalActionsResponse =
                            await fetchGoalActions(homeless_id);
                        const { goal_actions } =
                            await goalActionsResponse.json();
                        const matchingGoalAction = goal_actions.find(
                            (goalAction: any) =>
                                goalAction.ctp_caseload_referral_id ===
                                caseload_referral_id
                        );

                        if (matchingGoalAction) {
                            dispatch(addNewGoalAction(matchingGoalAction));
                        } else {
                            console.error('No matching goal action found');
                        }
                    } catch {
                        console.error('Failed to fetch goal actions');
                    }
                } else {
                    dispatch(referMemberFailAction());
                    console.error(
                        'Caseload referral ID not found in the response'
                    );
                }
            } else {
                dispatch(referMemberFailAction());
                console.error('Failed to refer member: Invalid response');
            }
        } catch (error) {
            dispatch(referMemberFailAction());
            console.error('Error during referral:', error);
        }
    };
}

export function referCareCoordinatorRequest({
    homeless_id,
    note,
    referee_id
}: {
    homeless_id: number;
    note?: string;
    referee_id: number;
}) {
    return async (dispatch: Function) => {
        dispatch(referCareCoordinatorAction());

        try {
            console.log('Sending care coordinator referral request:', { homeless_id, note, referee_id });
            const response = await api.referCareCoordinator({
                homeless_id,
                note,
                referee_id
            });

            if (response && response.ok) {
                const responseJson = await response.json();
                console.log('Care coordinator referral response:', responseJson);
                const {
                    care_coordinator_referral: { id: care_coordinator_referral_id } = {
                        id: null
                    }
                } = responseJson;

                if (care_coordinator_referral_id) {
                    dispatch(referCareCoordinatorSuccessAction());
                    dispatch(
                        updateMemberReferrals(
                            responseJson.care_coordinator_referral
                        )
                    );
                } else {
                    console.error('Care coordinator referral response missing ID:', responseJson);
                    dispatch(referCareCoordinatorFailAction());
                    console.error(
                        'Care coordinator referral ID not found in the response'
                    );
                }
            } else {
                console.error('Care coordinator referral failed with status:', response.status);
                const errorText = await response.text();
                console.error('Error response:', errorText);
                dispatch(referCareCoordinatorFailAction());
                console.error('Failed to refer care coordinator: Invalid response');
            }
        } catch (error) {
            console.error('Care coordinator referral error details:', error);
            dispatch(referCareCoordinatorFailAction());
            console.error('Error during care coordinator referral:', error);
        }
    };
}